import { z } from 'zod';

export const MediaAssetTypeEnum = z.enum(['cover', 'image', 'video', 'audio']);
export type MediaAssetType = z.infer<typeof MediaAssetTypeEnum>;
export const MediaProcessingStatusEnum = z.enum(['ready', 'processing', 'failed']);
export type MediaProcessingStatus = z.infer<typeof MediaProcessingStatusEnum>;
export const SUPPORTED_VIDEO_MIME_TYPES = ['video/mp4'] as const;
export type SupportedVideoMimeType = (typeof SUPPORTED_VIDEO_MIME_TYPES)[number];
export const ADMIN_SOURCE_VIDEO_MIME_TYPES = ['video/mp4', 'video/quicktime'] as const;
export type AdminSourceVideoMimeType = (typeof ADMIN_SOURCE_VIDEO_MIME_TYPES)[number];

// Allowed MIME types per asset type
export const ALLOWED_MIME_TYPES: Record<MediaAssetType, string[]> = {
  cover: ['image/jpeg', 'image/png', 'image/webp'],
  image: ['image/jpeg', 'image/png', 'image/webp'],
  video: [...ADMIN_SOURCE_VIDEO_MIME_TYPES],
  audio: ['audio/mpeg', 'audio/mp4', 'audio/ogg'],
};

// Max file sizes in bytes
export const MAX_FILE_SIZES: Record<MediaAssetType, number> = {
  cover: 5 * 1024 * 1024, // 5 MB
  image: 10 * 1024 * 1024, // 10 MB
  video: 500 * 1024 * 1024, // 500 MB
  audio: 50 * 1024 * 1024, // 50 MB
};

export const AdminPresignRequestSchema = z.object({
  type: MediaAssetTypeEnum,
  mimeType: z.string().min(1),
  sizeBytes: z.number().int().positive(),
  fileName: z.string().min(1).max(255),
});

export type AdminPresignRequest = z.infer<typeof AdminPresignRequestSchema>;

export const AdminPresignResponseSchema = z.object({
  uploadUrl: z.string().url(),
  storageKey: z.string(),
  expiresAt: z.string(), // ISO8601
});

export type AdminPresignResponse = z.infer<typeof AdminPresignResponseSchema>;

export const AdminFinalizeMediaSchema = z.object({
  storageKey: z.string().min(1),
  type: MediaAssetTypeEnum,
  mimeType: z.string().min(1),
  sizeBytes: z.number().int().positive(),
  durationSeconds: z.number().positive().optional(),
});

export type AdminFinalizeMediaDto = z.infer<typeof AdminFinalizeMediaSchema>;

export const AdminMediaAssetResponseSchema = z.object({
  id: z.string(),
  type: z.string(),
  storageKey: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number(),
  durationSeconds: z.number().nullable(),
  publicUrl: z.string().nullable(),
  processingStatus: MediaProcessingStatusEnum,
  sourceStorageKey: z.string().nullable(),
  sourceMimeType: z.string().nullable(),
  derivativeStorageKey: z.string().nullable(),
  derivativeMimeType: z.string().nullable(),
  derivativeSizeBytes: z.number().nullable(),
  processingError: z.string().nullable(),
  createdAt: z.string(),
});

export type AdminMediaAssetResponse = z.infer<typeof AdminMediaAssetResponseSchema>;
